// src/components/InteractiveContent.tsx
//
// Renders chat messages with inline interactive chips.
//
// Strategy (important — previous attempts had bugs):
//   1. Interactive markup `[text](category:id)` is tokenized OUT of
//      the content BEFORE ReactMarkdown sees it. Each chip gets
//      replaced with a short unicode placeholder (e.g. `§§0§§`).
//   2. ReactMarkdown renders the placeholder-ified text, handling all
//      normal markdown (paragraphs, lists, bold, links, etc).
//   3. A custom `p`/`li`/`strong` renderer walks the produced children
//      looking for placeholder strings and splices in chip components.
//
// Why not use the `a` renderer trick? Because ReactMarkdown rendered
// the interactive URL as an `<a href="...">` and browsers (especially
// mobile Safari + PWAs with a service worker) would follow the href
// BEFORE our React click handler could call `preventDefault`, causing
// a page reload. Placeholders don't have that problem: they're just
// text strings until we replace them, and our chip is a `<button>`
// with no href.

import React, { useCallback, useMemo, useState } from "react";
import { Box, Button, styled } from "@mui/material";
import {
  Person,
  Place,
  Event,
  Architecture,
  School,
  History,
  Info,
  QuestionMark,
  Explore,
  Science,
} from "@mui/icons-material";
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import {
  parseInteractiveContent,
  parsePlacesResults,
  parseTourThemes,
  parseMuseumOptions,
  parseEntityImages,
  parseThenAndNow,
  parseTimeline,
  parseArtworkZoom,
  parseNavigateTo,
  generateFollowUpMessage,
  extractLocationContext,
  cleanContentForDisplay,
  InteractiveElement,
  FollowUpQuestion,
} from "../utils/interactiveContent";
import { EntityDetailSheet } from "./EntityDetailSheet";
import { PlacesResultCard } from "./places/PlacesResultCard";
import { TourThemesCard } from "./walkingTour/TourThemesCard";
import { MuseumOptionsCard } from "./museumGuide/MuseumOptionsCard";
import { InlineEntityImageList } from "./InlineEntityImage";
import { ThenAndNowCard } from "./then-and-now/ThenAndNowCard";
import { TimelineCard } from "./timeline/TimelineCard";
import { ArtworkZoomCard } from "./artwork-zoom/ArtworkZoomCard";
import { NavigateToCard } from "./navigate/NavigateToCard";

// ──────────────────────────────────────────────────────────
// Chip visuals
// ──────────────────────────────────────────────────────────

const CATEGORY_COLOR: Record<string, string> = {
  person: "#7c3aed",
  place: "#059669",
  event: "#d97706",
  architecture: "#0284c7",
  concept: "#db2777",
  period: "#7c3aed",
};

const InlineChip = styled("button", {
  shouldForwardProp: (prop) => prop !== "categoryColor",
})<{ categoryColor: string }>(({ categoryColor, theme }) => ({
  display: "inline",
  verticalAlign: "baseline",
  background: "transparent",
  border: "none",
  padding: 0,
  margin: 0,
  cursor: "pointer",
  font: "inherit",
  color: categoryColor,
  textDecoration: "underline",
  textDecorationThickness: "1px",
  textDecorationColor: `${categoryColor}66`,
  textUnderlineOffset: "2px",
  transition: "text-decoration-color 0.15s",
  "&:hover": {
    textDecorationColor: categoryColor,
  },
  "&:focus-visible": {
    outline: `2px solid ${categoryColor}`,
    outlineOffset: "2px",
    borderRadius: 2,
  },
  "& .chip-icon": {
    fontSize: "0.85em",
    marginRight: "0.15em",
    verticalAlign: "-0.15em",
    opacity: 0.7,
  },
  [theme.breakpoints.down("sm")]: {
    padding: "2px 0",
  },
}));

const getCategoryIcon = (category: string): React.ReactNode => {
  const props = { className: "chip-icon", fontSize: "inherit" as const };
  switch (category) {
    case "person":
      return <Person {...props} />;
    case "place":
      return <Place {...props} />;
    case "event":
      return <Event {...props} />;
    case "architecture":
      return <Architecture {...props} />;
    case "concept":
      return <School {...props} />;
    case "period":
      return <History {...props} />;
    default:
      return <Info {...props} />;
  }
};

// ──────────────────────────────────────────────────────────
// Follow-up questions (unchanged behaviour, tightened visuals)
// ──────────────────────────────────────────────────────────

const getFollowUpIcon = (category: string) => {
  switch (category) {
    case "historical":
      return <History fontSize="small" />;
    case "technical":
      return <Science fontSize="small" />;
    case "cultural":
      return <Explore fontSize="small" />;
    case "practical":
      return <Info fontSize="small" />;
    default:
      return <QuestionMark fontSize="small" />;
  }
};

const FollowUpButton = styled(Button)(({ theme }) => ({
  borderRadius: 16,
  textTransform: "none",
  fontSize: "0.82rem",
  padding: theme.spacing(0.4, 1.25),
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.text.secondary,
  fontWeight: 400,
  lineHeight: 1.4,
  transition: "all 0.15s",
  "&:hover": {
    borderColor: theme.palette.text.primary,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "none",
  },
  "& .MuiButton-startIcon": {
    fontSize: 14,
    marginRight: theme.spacing(0.5),
    opacity: 0.6,
  },
}));

const FollowUpContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  paddingTop: theme.spacing(1.5),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

const MarkdownRoot = styled(Box)(({ theme }) => ({
  wordBreak: "break-word",
  lineHeight: 1.55,
  "& > :first-of-type": { marginTop: 0 },
  "& > :last-child": { marginBottom: 0 },
  "& p": {
    margin: theme.spacing(0.75, 0),
  },
  "& ul, & ol": {
    margin: theme.spacing(0.75, 0),
    paddingLeft: theme.spacing(2.5),
  },
  "& li": {
    marginBottom: theme.spacing(0.25),
  },
  "& strong": {
    fontWeight: 600,
  },
  "& a": {
    color: theme.palette.primary.main,
  },
  "& code": {
    padding: "0.1em 0.35em",
    backgroundColor: theme.palette.action.hover,
    borderRadius: 3,
    fontSize: "0.9em",
  },
  "& h1, & h2, & h3, & h4": {
    margin: theme.spacing(1.25, 0, 0.5),
    fontWeight: 600,
    lineHeight: 1.3,
  },
  "& h1": { fontSize: "1.2rem" },
  "& h2": { fontSize: "1.1rem" },
  "& h3": { fontSize: "1.02rem" },
  "& h4": { fontSize: "1rem" },
}));

// ──────────────────────────────────────────────────────────
// Tokenization
// ──────────────────────────────────────────────────────────

// A placeholder that's extremely unlikely to appear naturally. Uses
// § (section sign) on either side of a digit index. ReactMarkdown
// treats it as plain text — no special Markdown meaning.
const PLACEHOLDER_RE = /§§(\d+)§§/g;

interface TokenTable {
  tokens: InteractiveElement[];
  /** Content with each chip markup replaced by `§§<index>§§`. */
  tokenized: string;
}

/**
 * Pull all interactive chip markup out of content, replacing each
 * with a `§§N§§` placeholder. Returns both the placeholder-ified
 * content and the token table.
 */
function tokenizeInteractive(content: string): TokenTable {
  const tokens: InteractiveElement[] = [];
  const pattern =
    /\[([^\]]+)\]\((person|place|event|architecture|concept|period):([^)]+)\)/g;

  const tokenized = content.replace(
    pattern,
    (fullMatch, text, category, identifier) => {
      const idx = tokens.length;
      tokens.push({
        text,
        category,
        identifier,
        originalText: fullMatch,
      });
      return `§§${idx}§§`;
    }
  );

  return { tokens, tokenized };
}

/**
 * Walks a ReactMarkdown-produced child list, replacing any text node
 * containing placeholders with a mixed array of strings + <InlineChip>
 * components. Non-string children are passed through untouched.
 */
function replacePlaceholdersInChildren(
  children: React.ReactNode,
  tokens: InteractiveElement[],
  onChipClick: (el: InteractiveElement) => void
): React.ReactNode {
  const childArray = React.Children.toArray(children);

  return childArray.flatMap((child, i) => {
    if (typeof child !== "string") return child;
    if (!child.includes("§§")) return child;

    // Split on placeholder boundaries, interleave with chip components
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    const re = new RegExp(PLACEHOLDER_RE.source, "g");

    while ((match = re.exec(child)) !== null) {
      const before = child.slice(lastIndex, match.index);
      if (before) parts.push(before);

      const tokenIdx = parseInt(match[1], 10);
      const token = tokens[tokenIdx];
      if (token) {
        const categoryColor = CATEGORY_COLOR[token.category] || "#4b5563";
        parts.push(
          <InlineChip
            key={`chip-${i}-${tokenIdx}`}
            type="button"
            categoryColor={categoryColor}
            onClick={(e) => {
              // Belt-and-braces: stop any default, stop propagation.
              // Prevents interaction with parent click/touch handlers
              // and prevents PWAs from treating it as navigation.
              e.preventDefault();
              e.stopPropagation();
              onChipClick(token);
            }}
            onPointerDown={(e) => {
              // iOS Safari sometimes fires click synthetically after
              // a touch sequence — blocking pointerdown as well keeps
              // things tight.
              e.stopPropagation();
            }}
            aria-label={`${token.category}: ${token.text}`}
            data-interactive={token.category}
          >
            {getCategoryIcon(token.category)}
            {token.text}
          </InlineChip>
        );
      } else {
        // Token missing (shouldn't happen) — preserve the placeholder
        parts.push(match[0]);
      }

      lastIndex = match.index + match[0].length;
    }

    const rest = child.slice(lastIndex);
    if (rest) parts.push(rest);

    return parts;
  });
}

// ──────────────────────────────────────────────────────────
// Main component
// ──────────────────────────────────────────────────────────

interface InteractiveContentProps {
  content: string;
  onInteractiveClick?: (message: string, element: InteractiveElement) => void;
  onFollowUpClick?: (prompt: string) => void;
  recentMessages?: any[];
  isUser?: boolean;
  userLocation?: { lat: number; lng: number };
}

export const InteractiveContent: React.FC<InteractiveContentProps> = ({
  content,
  onInteractiveClick,
  onFollowUpClick,
  recentMessages = [],
  isUser = false,
  userLocation,
}) => {
  const [modalElement, setModalElement] =
    useState<InteractiveElement | null>(null);

  const handleChipClick = useCallback((element: InteractiveElement) => {
    setModalElement(element);
  }, []);

  const sendAsChatMessage = useCallback(
    (_message: string, element: InteractiveElement) => {
      if (!onInteractiveClick) return;
      const locationContext = extractLocationContext(recentMessages);
      const followUpMessage = generateFollowUpMessage(element, locationContext);
      onInteractiveClick(followUpMessage, element);
    },
    [onInteractiveClick, recentMessages]
  );

  // Pull chip tokens out and get placeholder-ified content
  const { tokens, tokenized } = useMemo(() => {
    const cleaned = cleanContentForDisplay(content);
    return tokenizeInteractive(cleaned);
  }, [content]);

  // Follow-up questions come from the raw content (separate block)
  const { followUpQuestions } = useMemo(
    () => parseInteractiveContent(content),
    [content]
  );

  // Places result block — structured "find me X" response. Same
  // pattern: parsed from the raw content, rendered as a card after
  // the prose. Gracefully empty during streaming (block incomplete)
  // and on parse failures.
  const placesItems = useMemo(() => parsePlacesResults(content), [content]);

  // Tour themes block — selectable walking-tour theme picks during
  // the awaiting_focus state. Tap a theme → routes through the same
  // follow-up channel (synthetic user message) so the AI can react
  // and emit the CONVERSATION_STATE marker that drives generation.
  const tourThemes = useMemo(() => parseTourThemes(content), [content]);
  const museumOptions = useMemo(() => parseMuseumOptions(content), [content]);

  // Inline image markers — [IMAGE](category:identifier) — that the AI
  // chose to embed alongside its prose. Resolved client-side via
  // Wikipedia. Rendered AFTER the prose body but BEFORE the places /
  // themes cards so reading flow is: text → image(s) → action card.
  const entityImages = useMemo(
    () => parseEntityImages(content),
    [content],
  );

  // Phase 5E — three new structured blocks for visual storytelling.
  // Each returns null when absent / malformed / mid-stream; the
  // renderer below mounts only when the parse succeeds.
  //
  //   THEN_AND_NOW — two images of the same subject across time.
  //   TIMELINE     — horizontal era picker for layered sites.
  //   ARTWORK_ZOOM — image with tappable regions of interest.
  //
  // All three render BEFORE the inline entity images block so the
  // page reading flow becomes: prose → structured visual block →
  // freeform images → action cards → follow-up chips. The
  // structured blocks anchor the response when present; the
  // freeform images supplement.
  const thenAndNow = useMemo(() => parseThenAndNow(content), [content]);
  const timeline = useMemo(() => parseTimeline(content), [content]);
  const artworkZoom = useMemo(() => parseArtworkZoom(content), [content]);

  // [NAVIGATE_TO] — the AI's mandatory routing-block emission.
  // Until this patch, chat-emitted NAVIGATE_TO blocks were
  // silently dropped (no chat-side renderer was wired; only
  // the reveal pre-armed path used NavigateToCard). Now any
  // chat response that satisfies the four-part mandatory rule
  // gets an inline NavigateToCard with bottom-sheet map.
  const navigateTarget = useMemo(
    () => parseNavigateTo(content),
    [content]
  );

  // Tap-through on a place row sends a follow-up message asking the
  // AI to elaborate. Reuses the existing onInteractiveClick wire.
  const handlePlaceTap = useCallback(
    (followUpMessage: string) => {
      if (onInteractiveClick) {
        // Pass undefined for the InteractiveElement param — we don't
        // have one; the chat handler accepts the prompt directly.
        onInteractiveClick(followUpMessage, undefined as never);
      }
    },
    [onInteractiveClick]
  );

  // Tap-through on a theme row sends a synthetic user message
  // committing to that theme. We deliberately use the follow-up
  // channel (not onInteractiveClick) so the message appears in
  // chat as if the user typed it — same visual effect as picking
  // any other follow-up chip. The AI's response will then include
  // the CONVERSATION_STATE marker that flips status to 'generating'.
  const handleThemeSelect = useCallback(
    (themeName: string) => {
      if (onFollowUpClick) {
        onFollowUpClick(`Let's go with the ${themeName} theme.`);
      }
    },
    [onFollowUpClick]
  );

  // Tap-through on a museum row commits the user's museum via the
  // same synthetic-user-message channel as theme picks. The AI's
  // reply emits the CONVERSATION_STATE marker that captures
  // museum_name and advances to awaiting_submode.
  const handleMuseumSelect = useCallback(
    (museumName: string) => {
      if (onFollowUpClick) {
        onFollowUpClick(`I'm at the ${museumName}.`);
      }
    },
    [onFollowUpClick]
  );

  // Memoize chip-replacement fn for stable component identity
  const replacePlaceholders = useCallback(
    (children: React.ReactNode) =>
      replacePlaceholdersInChildren(children, tokens, handleChipClick),
    [tokens, handleChipClick]
  );

  // Custom renderers: wrap all inline-containing block elements to
  // catch placeholders inside them.
  const mdComponents: Components = useMemo(
    () => ({
      p: ({ children, ...rest }) => (
        <p {...rest}>{replacePlaceholders(children)}</p>
      ),
      li: ({ children, ...rest }) => (
        <li {...rest}>{replacePlaceholders(children)}</li>
      ),
      strong: ({ children, ...rest }) => (
        <strong {...rest}>{replacePlaceholders(children)}</strong>
      ),
      em: ({ children, ...rest }) => (
        <em {...rest}>{replacePlaceholders(children)}</em>
      ),
      h1: ({ children, ...rest }) => (
        <h1 {...rest}>{replacePlaceholders(children)}</h1>
      ),
      h2: ({ children, ...rest }) => (
        <h2 {...rest}>{replacePlaceholders(children)}</h2>
      ),
      h3: ({ children, ...rest }) => (
        <h3 {...rest}>{replacePlaceholders(children)}</h3>
      ),
      h4: ({ children, ...rest }) => (
        <h4 {...rest}>{replacePlaceholders(children)}</h4>
      ),
      // Real Markdown links — open in a new tab
      a: ({ href, children, ...rest }) => (
        <a {...rest} href={href} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      ),
    }),
    [replacePlaceholders]
  );

  if (isUser) {
    return (
      <MarkdownRoot>
        <ReactMarkdown>{cleanContentForDisplay(content)}</ReactMarkdown>
      </MarkdownRoot>
    );
  }

  const conversationContext =
    recentMessages && recentMessages.length > 0
      ? recentMessages
          .slice(-3)
          .map((m) => (typeof m.content === "string" ? m.content : ""))
          .filter(Boolean)
          .join(" ")
          .slice(0, 500)
      : undefined;

  return (
    <>
      <MarkdownRoot>
        <ReactMarkdown components={mdComponents}>{tokenized}</ReactMarkdown>

        {/* Phase 5E — structured visual blocks. Each mounts only
            when the corresponding parse succeeded (null guards).
            Order matters: THEN_AND_NOW first (most directly tied
            to a specific factual claim the prose just made),
            TIMELINE second (broader-context), ARTWORK_ZOOM third
            (typically tied to art the prose just discussed).
            All three sit BEFORE the freeform entity images so
            the structured response anchors the reading flow. */}
        {thenAndNow && <ThenAndNowCard block={thenAndNow} />}
        {timeline && (
          <TimelineCard block={timeline} onEraFollowUp={onFollowUpClick} />
        )}
        {artworkZoom && <ArtworkZoomCard block={artworkZoom} />}

        {/* [NAVIGATE_TO] — mounts inline in the chat message
            stream when the AI emits the block. Uncontrolled
            mode: NavigateToCard owns the open/close state for
            its bottom-sheet (CollectionRevealCard takes the
            controlled path with its own chip trigger; this
            uncontrolled path shows the inline teaser + auto-
            opens to the sheet on tap). */}
        {navigateTarget && <NavigateToCard target={navigateTarget} />}

        {/* Inline entity images — Wikipedia thumbnails for things the
            AI explicitly wants to show (paintings, landmarks, people).
            Resolves async; renders nothing for entities Wikipedia
            doesn't have, so the prose isn't disrupted by phantom
            placeholders. Sits between prose and action cards. */}
        {entityImages.length > 0 && (
          <InlineEntityImageList refs={entityImages} />
        )}

        {/* Tour themes card — selectable picker shown when the AI
            offers walking-tour theme options during the
            awaiting_focus state. Tap a theme → AI gets a synthetic
            user message → emits CONVERSATION_STATE → generator
            kicks off. Mounts only when a [TOUR_THEMES] block parsed
            successfully. */}
        {tourThemes.length > 0 && onFollowUpClick && (
          <TourThemesCard items={tourThemes} onSelect={handleThemeSelect} />
        )}

        {/* Museum options card — selectable nearby-museum picker
            shown during a museum_guide conversation's
            awaiting_museum state. Tap a museum → synthetic user
            message → AI emits CONVERSATION_STATE capturing the
            museum and asks guided vs free roam. */}
        {museumOptions.length > 0 && onFollowUpClick && (
          <MuseumOptionsCard
            items={museumOptions}
            onSelect={handleMuseumSelect}
          />
        )}

        {/* Places result card — structured response for "find me X"
            queries. Mounts when the AI has emitted a complete
            [PLACES_RESULT] block and its JSON parses. Each item is
            grounded against Google Places client-side. */}
        {placesItems.length > 0 && (
          <PlacesResultCard
            items={placesItems}
            userLocation={userLocation}
            onInteractiveClick={handlePlaceTap}
          />
        )}

        {followUpQuestions.length > 0 && onFollowUpClick && (
          <FollowUpContainer>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
              }}
            >
              {followUpQuestions.map((q: FollowUpQuestion, i) => (
                <FollowUpButton
                  key={`followup-${i}`}
                  startIcon={getFollowUpIcon(q.category)}
                  onClick={() => onFollowUpClick(q.prompt)}
                  size="small"
                  variant="outlined"
                >
                  {q.text}
                </FollowUpButton>
              ))}
            </Box>
          </FollowUpContainer>
        )}
      </MarkdownRoot>

      <EntityDetailSheet
        open={!!modalElement}
        onClose={() => setModalElement(null)}
        element={modalElement}
        userLocation={userLocation}
        conversationContext={conversationContext}
        onAskInChat={onInteractiveClick ? sendAsChatMessage : undefined}
      />
    </>
  );
};
